import { useState, useEffect } from "react";
import siteConfig from "../../config/site";
import Footer from "../../Components/Footer/Footer";

const API_URL = siteConfig.apiUrl;

const BookNow = () => {
  const { services } = siteConfig;

  // Fetch entertainers from API
  const [entertainers, setEntertainers] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/entertainers`)
      .then(r => r.json())
      .then(data => {
        setEntertainers((data.entertainers || []).map(e => ({
          id: e.name.toLowerCase(),
          name: e.name,
        })));
      })
      .catch(() => {
        // Fallback to config
        setEntertainers(siteConfig.entertainers || []);
      });
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    eventType: "",
    guests: "",
    performers: [],
    budget: "",
    requests: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePerformerToggle = (performerId) => {
    setFormData((prev) => ({
      ...prev,
      performers: prev.performers.includes(performerId)
        ? prev.performers.filter((id) => id !== performerId)
        : [...prev.performers, performerId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    try {
      const res = await fetch(`${API_URL}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          date: formData.date,
          eventType: formData.eventType,
          guests: formData.guests,
          performers: formData.performers,
          budget: formData.budget,
          requests: formData.requests,
        }),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.error || "Something went wrong. Please try again or email us directly.");
      }
    } catch {
      setSubmitError("Unable to submit. Please try again or email booking@exoentertainment.ca directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClasses =
    "w-full bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 transition-all";
  const labelClasses = "block text-gray-300 text-sm font-medium mb-2";

  if (submitted) {
    return (
      <div className="bg-[#0a0a0f] min-h-screen">
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-6xl mb-6">✨</div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Inquiry Received
            </h1>
            <p className="text-gray-400 text-lg mb-8">
              Thank you for your booking inquiry. We'll get back to you within a few hours.
              For immediate assistance, contact us directly.
            </p>
            <a
              href="mailto:booking@exoentertainment.ca"
              className="inline-block px-8 py-3 bg-gold text-black font-bold rounded-full hover:bg-gold-light transition-all duration-300"
            >
              booking@exoentertainment.ca
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      {/* Header */}
      <div className="pt-32 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            Book Your Experience
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-6" />
          <p className="text-gray-400 text-lg">
            Fill out the form below and we'll confirm your booking within hours.
          </p>

          {/* Availability badge */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-900/20 border border-green-800/30">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-medium">Available 24/7</span>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="px-6 pb-20">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full name"
                  required
                  className={inputClasses}
                />
              </div>
              <div>
                <label className={labelClasses}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  className={inputClasses}
                />
              </div>
            </div>

            {/* Phone & Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(403) 000-0000"
                  className={inputClasses}
                />
              </div>
              <div>
                <label className={labelClasses}>Preferred Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                />
              </div>
            </div>

            {/* Event Type */}
            <div>
              <label className={labelClasses}>Event Type *</label>
              <select
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
                className={inputClasses}
              >
                <option value="">Select a service...</option>
                {services.map((service, idx) => (
                  <option key={idx} value={service.name}>
                    {service.name} — {service.price}
                  </option>
                ))}
              </select>
            </div>

            {/* Number of Guests */}
            <div>
              <label className={labelClasses}>Number of Guests</label>
              <input
                type="number"
                name="guests"
                value={formData.guests}
                onChange={handleChange}
                placeholder="1"
                min="1"
                max="50"
                className={inputClasses}
              />
            </div>

            {/* Preferred Performer(s) */}
            <div>
              <label className={labelClasses}>Preferred Performer(s)</label>
              <div className="flex flex-wrap gap-3">
                {entertainers.map((performer) => (
                  <button
                    key={performer.id}
                    type="button"
                    onClick={() => handlePerformerToggle(performer.id)}
                    className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
                      formData.performers.includes(performer.id)
                        ? "bg-purple-700 border-purple-600 text-white"
                        : "bg-gray-900/60 border-gray-700/50 text-gray-400 hover:border-purple-600 hover:text-purple-400"
                    }`}
                  >
                    {performer.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handlePerformerToggle("no-preference")}
                  className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
                    formData.performers.includes("no-preference")
                      ? "bg-purple-700 border-purple-600 text-white"
                      : "bg-gray-900/60 border-gray-700/50 text-gray-400 hover:border-purple-600 hover:text-purple-400"
                  }`}
                >
                  No Preference
                </button>
              </div>
            </div>

            {/* Budget Range */}
            <div>
              <label className={labelClasses}>Budget Range</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className={inputClasses}
              >
                <option value="">Select budget range...</option>
                <option value="under-200">Under $200</option>
                <option value="200-400">$200 – $400</option>
                <option value="400-700">$400 – $700</option>
                <option value="700-plus">$700+</option>
                <option value="custom">Custom / Contact Me</option>
              </select>
            </div>

            {/* Special Requests */}
            <div>
              <label className={labelClasses}>Special Requests or Notes</label>
              <textarea
                name="requests"
                value={formData.requests}
                onChange={handleChange}
                placeholder="Tell us about your event, any special requirements, preferred location, etc."
                rows="4"
                className={inputClasses}
              />
            </div>

            {/* Error */}
            {submitError && (
              <div className="p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-400 text-sm">
                {submitError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-gold text-black font-bold text-lg rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Submit Booking Inquiry"}
            </button>

            <p className="text-gray-500 text-sm text-center">
              We'll respond within a few hours. For immediate requests, email{" "}
              <a href="mailto:booking@exoentertainment.ca" className="text-gold hover:text-gold-light">
                booking@exoentertainment.ca
              </a>
            </p>
          </form>
        </div>
      </section>

      {/* Direct Contact */}
      <section className="px-6 pb-16">
        <div className="max-w-2xl mx-auto">
          <div className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/30 text-center">
            <h3 className="text-xl font-bold text-white mb-2">
              Prefer to Contact Us Directly?
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Available 24/7 for booking inquiries
            </p>
            <a
              href="mailto:booking@exoentertainment.ca"
              className="text-gold hover:text-gold-light font-semibold text-lg transition-colors"
            >
              booking@exoentertainment.ca
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookNow;
